'use client'

import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react'
import { metalRatesApiService } from '@/lib/metal-rates-api'
import { useAuthStore } from '@/stores/auth-store'

interface MetalRate {
  metal: string
  price: number
  change: number
  changePercent: number
  lastUpdate: string
  unit: string
}

interface MetalRatesContextType {
  rates: MetalRate[]
  loading: boolean
  isInitialized: boolean
  refreshRates: () => Promise<void>
  error: string | null
}

const MetalRatesContext = createContext<MetalRatesContextType | undefined>(undefined)

export const useMetalRates = () => {
  const context = useContext(MetalRatesContext)
  
  if (context === undefined) {
    // Return a safe default instead of throwing an error
    return {
      rates: [],
      loading: false,
      isInitialized: true, // Changed from false to true to allow components to proceed
      refreshRates: async () => {
        console.warn('MetalRates provider not ready - refreshRates called before initialization');
      },
      error: null
    }
  }
  return context
}

export const MetalRatesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rates, setRates] = useState<MetalRate[]>([])
  const [loading, setLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [providerReady, setProviderReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated, isInitialized: authInitialized } = useAuthStore()

  // Ensure we only run client-side code after mounting
  useEffect(() => {
    setMounted(true)
  }, [])

  const refreshRates = useCallback(async () => {
    try {
      // Only run in browser environment
      if (typeof window === 'undefined') return
      
      setLoading(true)
      setError(null)
      
      const response = await metalRatesApiService.getMetalRates()
      
      if (response.success) {
        setRates(response.data)
      } else {
        setError(response.error || 'Failed to fetch metal rates')
      }
    } catch (error) {
      console.error('Failed to refresh metal rates:', error)
      setError('Failed to fetch metal rates')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Only run in browser environment and after mounting
    if (typeof window === 'undefined' || !mounted) {
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
      return
    }

    // Wait for auth to be initialized before proceeding
    if (!authInitialized) {
      return
    }

    // Only load metal rates if user is authenticated
    if (!isAuthenticated) {
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
      return
    }

    // Load metal rates
    const loadRates = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await metalRatesApiService.getMetalRates()
        
        if (response.success) {
          setRates(response.data)
        } else {
          setError(response.error || 'Failed to fetch metal rates')
        }
      } catch (error) {
        console.error('Failed to load metal rates:', error)
        setError('Failed to fetch metal rates')
      } finally {
        setLoading(false)
        setIsInitialized(true)
        setProviderReady(true)
      }
    }

    loadRates().catch(error => {
      console.error('Metal rates initialization failed:', error)
      // Ensure we still set ready state even on error
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
    })
  }, [mounted, isAuthenticated, authInitialized]) // Add auth dependencies

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    rates,
    loading,
    isInitialized,
    refreshRates,
    error
  }), [rates, loading, isInitialized, refreshRates, error])

  // Always render children to prevent hydration mismatch
  return (
    <MetalRatesContext.Provider value={value}>
      {children}
    </MetalRatesContext.Provider>
  )
} 